import { useCustomAudiencesApi, useShowSpinner } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { ADD_DESTINATIONS_AVAILABLE } from '../../featureFlags';
import useCustomAudiencesRequest from '../../hooks/useCustomAudiencesRequest';
import useVisiblePolling, { useBoundedPollInterval } from '../../hooks/useVisiblePolling';
import { deleteBlockedReason } from '../../lib/audienceDelete';
import {
  deriveStatusFromLookups,
  statusHoverText,
  statusKeepsPolling
} from '../../lib/buildAudienceRows';
import { hasDestinationsInFlight } from '../../lib/deliverySummary';
import { logError } from '../../lib/logError';
import { parseDefinitionId } from '../../lib/parseDefinitionId';
import { SNAPSHOTS_PAGE_SIZE, selectTargetsBlocker } from '../../store/customAudiencesSlice';
import useCustomAudiencesStore from '../../store/useCustomAudiencesStore';
import type { AudienceDelivery, AudienceDestination } from '../../types';
import DefinitionDetailsView, { type DetailsMode, type DetailsTab } from './DefinitionDetails.View';

const NO_DESTINATIONS: AudienceDestination[] = [];
const NO_DELIVERIES: AudienceDelivery[] = [];

const DefinitionDetails = () => {
  const ctx = useCustomAudiencesApi();
  const navigate = useNavigate();
  const request = useCustomAudiencesRequest();
  const { definitionId: definitionIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedId = parseDefinitionId(definitionIdParam);
  const validId = parsedId != null;
  /* No definition has id 0, so an invalid route id selects nothing */
  const definitionId = parsedId ?? 0;
  /* Deliveries is the default tab, so it carries no tab param */
  const tabParam = searchParams.get('tab');
  const activeTab: DetailsTab =
    tabParam === 'definition' || tabParam === 'snapshots' ? tabParam : 'deliveries';
  /*
   * The edit form and the Deliver-to panel are exclusive, so one mode value holds which is open.
   * Leaving delivering mode by any path discards the panel's selection and errors.
   */
  const [mode, setMode] = useState<DetailsMode>('idle');

  const state = useCustomAudiencesStore(
    useShallow(store => ({
      selectedDefinition: store.selectedDefinition,
      selectedDefinitionStatus: store.selectedDefinitionStatus,
      selectedDefinitionError: store.selectedDefinitionError,
      updatingDefinitionId: store.updatingDefinitionId,
      snapshots: store.snapshots,
      snapshotsTotal: store.snapshotsTotal,
      snapshotsPageNumber: store.snapshotsPageNumber,
      latestSnapshot: store.latestSnapshotByDefinitionId[definitionId] ?? null,
      destinations: store.destinationsByDefinitionId[definitionId] ?? NO_DESTINATIONS,
      deliveriesBySnapshotId: store.deliveriesBySnapshotId,
      destinationsError: store.destinationsErrorByDefinitionId[definitionId] ?? null,
      deletePreview:
        store.deletePreview?.definitionId === definitionId ? store.deletePreview : null,
      destinationsLoaded: definitionId in store.destinationsByDefinitionId,
      deliveriesErrorBySnapshotId: store.deliveriesErrorBySnapshotId,
      snapshotsError: store.snapshotsError,
      adAccounts: store.adAccounts,
      adAccountsError: store.adAccountsError,
      tosCheckByAdAccountId: store.tosCheckByAdAccountId,
      recheckAdAccountTos: store.recheckAdAccountTos,
      targets: store.targets,
      targetsBlocker: selectTargetsBlocker(store),
      deliverySubmitting: store.deliverySubmitting,
      loadDefinitionDetails: store.loadDefinitionDetails,
      loadSnapshots: store.loadSnapshots,
      loadAdAccounts: store.loadAdAccounts,
      setDefinitionStatus: store.setDefinitionStatus,
      loadDeletePreview: store.loadDeletePreview,
      clearDeletePreview: store.clearDeletePreview,
      deleteDefinition: store.deleteDefinition,
      updateDefinitionFields: store.updateDefinitionFields,
      addDestinations: store.addDestinations,
      setTargetPlatform: store.setTargetPlatform,
      toggleTargetAdAccount: store.toggleTargetAdAccount,
      toggleLookalikeRatio: store.toggleLookalikeRatio,
      setTargetValueMetric: store.setTargetValueMetric,
      resetTargets: store.resetTargets
    }))
  );
  const { loadDefinitionDetails, loadAdAccounts, resetTargets, clearDeletePreview } = state;
  const [deleteOpen, setDeleteOpen] = useState(false);

  const pollKey = `${ctx.brandId}:${definitionId}`;
  /* When this definition's details last finished loading, keyed so another definition's time never shows */
  const [lastChecked, setLastChecked] = useState<{ key: string; at: Date } | null>(null);
  const markChecked = () => setLastChecked({ key: pollKey, at: new Date() });

  // biome-ignore lint/correctness/useExhaustiveDependencies: markChecked only stamps this load's key
  useEffect(() => {
    if (!ctx.brandId || !validId) return;
    loadDefinitionDetails(ctx, definitionId)
      .then(markChecked)
      .catch(error => logError('details.load', error));
    loadAdAccounts(ctx).catch(error => logError('details.adAccounts', error));
  }, [ctx, definitionId, validId, loadDefinitionDetails, loadAdAccounts]);

  /* A half-filled Deliver-to panel belongs to this definition only */
  useEffect(() => resetTargets, [resetTargets]);
  useEffect(() => clearDeletePreview, [clearDeletePreview]);

  const definition =
    state.selectedDefinition?.id === definitionId ? state.selectedDefinition : null;

  const {
    latestSnapshot,
    destinations,
    destinationsError,
    destinationsLoaded,
    deliveriesBySnapshotId,
    deliveriesErrorBySnapshotId
  } = state;
  /* The latest run's delivery attempts, which the status and the Deliveries table both read */
  const deliveries =
    (latestSnapshot ? deliveriesBySnapshotId[latestSnapshot.id] : undefined) ?? NO_DELIVERIES;
  const status = useMemo(() => {
    if (!definition) return null;
    return deriveStatusFromLookups(definition, latestSnapshot, destinations, deliveries, [
      { error: destinationsError, hasLastGood: destinationsLoaded },
      {
        error: latestSnapshot ? deliveriesErrorBySnapshotId[latestSnapshot.id] : undefined,
        hasLastGood: latestSnapshot != null && latestSnapshot.id in deliveriesBySnapshotId
      }
    ]);
  }, [
    definition,
    latestSnapshot,
    destinations,
    destinationsError,
    destinationsLoaded,
    deliveries,
    deliveriesBySnapshotId,
    deliveriesErrorBySnapshotId
  ]);
  const derived = status?.derived ?? null;

  /* A paused audience can still have destinations populating, so check them directly too */
  const polling =
    (status != null && statusKeepsPolling(status)) ||
    hasDestinationsInFlight(state.destinations, deliveries);

  useShowSpinner({ show: state.selectedDefinitionStatus === 'loading' && !definition });
  const { intervalMs } = useBoundedPollInterval(polling, pollKey);
  useVisiblePolling(
    signal =>
      loadDefinitionDetails({ ...ctx, signal }, definitionId, { refresh: true })
        .then(() => {
          if (!signal.aborted) markChecked();
        })
        .catch(error => logError('details.poll', error)),
    intervalMs,
    polling,
    pollKey
  );

  const viewerPath = `/brand/${ctx.brandId}/custom-audiences`;

  const onSetPaused = async (paused: boolean) => {
    await request.run(
      'status',
      () => state.setDefinitionStatus(ctx, definitionId, paused ? 'paused' : 'active'),
      {
        success: paused ? 'Audience paused' : 'Audience resumed',
        error: "Couldn't update the audience"
      }
    );
  };

  const loadDeletePreview = () =>
    state
      .loadDeletePreview(ctx, definitionId)
      .catch(error => logError('details.deletePreview', error));

  const onOpenDelete = () => {
    setDeleteOpen(true);
    loadDeletePreview();
  };

  const onCloseDelete = () => {
    setDeleteOpen(false);
    clearDeletePreview();
  };

  /* The dialog closes on any settled outcome, since the page reloads to show what the delete did */
  const onConfirmDelete = async () => {
    const result = await request.run('delete', () => state.deleteDefinition(ctx, definitionId), {
      success: 'Audience deleted',
      error: "Couldn't delete the audience"
    });
    if (result) onCloseDelete();
  };

  const onSaveDetails = async (name: string, description: string) => {
    const result = await request.run(
      'details',
      () =>
        state.updateDefinitionFields(ctx, definitionId, {
          name: name.trim(),
          /* A cleared description is stored as null, as a definition created without one is */
          description: description.trim() || null
        }),
      { success: 'Saved', error: "Couldn't save" }
    );
    return Boolean(result?.data);
  };

  const onTabChange = (tab: DetailsTab) =>
    setSearchParams(tab === 'deliveries' ? {} : { tab }, { replace: true });

  const discardDelivery = () => {
    request.clearError('deliver');
    resetTargets();
  };

  const onStartEdit = () => {
    if (mode === 'delivering') discardDelivery();
    setMode('editing');
  };

  const onStartDeliver = () => setMode('delivering');

  const onCancelMode = () => {
    if (mode === 'delivering') discardDelivery();
    setMode('idle');
  };

  /* Deliver shows its progress and errors on the Deliveries tab, so it switches there first */
  const onDeliver = async () => {
    if (activeTab !== 'deliveries') onTabChange('deliveries');
    const result = await request.run('deliver', () => state.addDestinations(ctx, definitionId), {
      success: inserted =>
        `Delivering to ${inserted.length} new ${inserted.length === 1 ? 'destination' : 'destinations'}`,
      error: "Couldn't deliver"
    });
    if (!result?.data) return;
    setMode('idle');
    loadDefinitionDetails(ctx, definitionId, { refresh: true }).catch(error =>
      logError('details.refreshAfterDeliver', error)
    );
  };

  return (
    <DefinitionDetailsView
      definition={definition}
      derived={derived}
      statusHoverText={status ? statusHoverText(status) : undefined}
      loading={state.selectedDefinitionStatus === 'loading'}
      error={validId ? state.selectedDefinitionError : `No audience with id "${definitionIdParam}"`}
      viewerPath={viewerPath}
      activeTab={activeTab}
      mode={mode}
      discardConfirm={
        mode === 'delivering' &&
        (state.targets.adAccountIds.length > 0 ||
          state.targets.lookalikeRatios.length > 0 ||
          state.targets.valueMetric != null)
      }
      updating={state.updatingDefinitionId === definitionId}
      snapshots={definition ? state.snapshots : []}
      snapshotsError={state.snapshotsError}
      snapshotsPageNumber={state.snapshotsPageNumber}
      snapshotsPageCount={Math.max(1, Math.ceil(state.snapshotsTotal / SNAPSHOTS_PAGE_SIZE))}
      deliveries={{
        destinations: state.destinations,
        deliveries,
        destinationsError: state.destinationsError?.message ?? null,
        adAccounts: state.adAccounts,
        adAccountsError: state.adAccountsError,
        tosCheckByAdAccountId: state.tosCheckByAdAccountId,
        onRecheckTos: adAccountId => {
          state
            .recheckAdAccountTos(ctx, adAccountId)
            .catch(error => logError('details.tosRecheck', error));
        },
        definitionStatus: definition?.status ?? 'active',
        /* proxima-audiences has no add-destinations route, so the live page hides Deliver to */
        canDeliver: ADD_DESTINATIONS_AVAILABLE,
        deliverOpen: mode === 'delivering',
        targets: state.targets,
        deliveryErrors: request.errorFor('deliver')?.validationErrors ?? [],
        onOpenDeliver: onStartDeliver,
        onSelectPlatform: state.setTargetPlatform,
        onToggleAdAccount: state.toggleTargetAdAccount,
        onToggleRatio: state.toggleLookalikeRatio,
        onSelectValueMetric: state.setTargetValueMetric
      }}
      deliverActions={{
        /* A paused definition keeps the form with Deliver disabled */
        blocker: definition?.status === 'paused' ? 'Resume to deliver' : state.targetsBlocker,
        submitting: state.deliverySubmitting,
        onDeliver
      }}
      deleteActions={{
        blockedReason: deleteBlockedReason(derived?.status ?? null),
        dialogOpen: deleteOpen,
        preview: state.deletePreview,
        deleting: request.isPending('delete'),
        onOpen: onOpenDelete,
        onRetryPreview: loadDeletePreview,
        onConfirm: onConfirmDelete,
        onClose: onCloseDelete
      }}
      onStartEdit={onStartEdit}
      onCancelMode={onCancelMode}
      onTabChange={onTabChange}
      onSnapshotsPageChange={pageNumber =>
        request.run('snapshots', () => state.loadSnapshots(ctx, definitionId, pageNumber), {
          error: "Couldn't load that page of runs"
        })
      }
      onSetPaused={onSetPaused}
      onSaveDetails={onSaveDetails}
      onClone={() => navigate(`${viewerPath}/new?cloneFrom=${definitionId}`)}
      pollStatus={
        polling ? { lastCheckedAt: lastChecked?.key === pollKey ? lastChecked.at : null } : null
      }
    />
  );
};

export default DefinitionDetails;
